import { getFarm, deleteFarm, updateFarm, addFarm } from '@/adapters/master';
import { IFarm, IFarmRes, IApiResponse } from '@/interfaces/master';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Flex,
  notification,
  Radio,
  Table,
  TableColumnsType,
  Input,
} from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { uniqueId } from 'lodash';

interface IBatchRejection {
  status: 'rejected' | 'fulfilled';
  reason?: { errors?: { errorMessage?: string } };
}

type FarmBatchData = Partial<IFarm>[];

const getErrorMessage = (r: unknown): string => {
  if (r instanceof Error) return r.message;
  if (typeof r === 'object' && r !== null && 'message' in r)
    return (r as { message: string }).message;
  return 'เกิดข้อผิดพลาดในการดำเนินการ';
};

const FarmPage: React.FC = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<IFarm[]>([]);
  const [selected, setSelected] = useState<IFarm[]>([]);
  const [tab, setTab] = useState<'create' | 'update'>('create');
  const [searchText, setSearchText] = useState('');

  const { data, refetch, isFetching } = useQuery<IFarmRes, Error>({
    queryKey: ['get-farm'],
    queryFn: getFarm,
    retry: 0,
  });

  const notifyBatchErrors = (
    title: string,
    batchResults: IBatchRejection[],
    originalData: FarmBatchData,
  ) => {
    const errors: string[] = [];
    if (Array.isArray(batchResults)) {
      batchResults.forEach((result, index) => {
        if (result.status === 'rejected') {
          const failedCode = originalData[index]?.FARM_ID || `Index: ${index}`;
          errors.push(
            `${failedCode}: ${result.reason?.errors?.errorMessage || 'ไม่ทราบสาเหตุ'}`,
          );
        }
      });
    }
    if (errors.length > 0) {
      notification.error({
        message: title,
        style: { width: '500px' },
        description: (
          <ul
            style={{
              paddingLeft: '15px',
              margin: 0,
              maxHeight: '250px',
              overflowY: 'auto',
            }}
          >
            {errors.map((err, i) => (
              <li key={i}>{err}</li>
            ))}
          </ul>
        ),
        duration: 8,
      });
      return true;
    }
    return false;
  };

  const actionFarmMutation = useMutation({
    mutationFn: (payload: FarmBatchData) =>
      tab === 'create' ? addFarm(payload) : updateFarm(payload),
    onSuccess: (res, variables) => {
      if (
        !notifyBatchErrors(
          tab === 'create' ? 'Sync Farm ไม่สำเร็จ' : 'Update Farm ไม่สำเร็จ',
          res.data as IBatchRejection[],
          variables,
        )
      ) {
        notification.success({
          message: tab === 'create' ? 'Sync Farm สำเร็จ' : 'Update Farm สำเร็จ',
        });
      }
      refetch();
    },
    onError: r => notification.error({ message: getErrorMessage(r) }),
  });

  const actionDeleteFarm = useMutation({
    mutationFn: (payload: FarmBatchData) => deleteFarm(payload),
    onSuccess: (res, variables) => {
      if (
        !notifyBatchErrors(
          'ลบข้อมูลไม่สำเร็จ',
          res.data as IBatchRejection[],
          variables,
        )
      ) {
        notification.success({ message: 'Delete Farm สำเร็จ' });
      }
      refetch();
    },
    onError: r => notification.error({ message: getErrorMessage(r) }),
  });

  const filteredData = useMemo(() => {
    if (!searchText) return selected;
    const lowerSearch = searchText.toLowerCase();
    return selected.filter(
      item =>
        item.FARM_ID?.toLowerCase().includes(lowerSearch) ||
        item.FARM_USER1?.toLowerCase().includes(lowerSearch) ||
        item.FRM_NAME?.toLowerCase().includes(lowerSearch) ||
        item.FARM_NAME?.toLowerCase().includes(lowerSearch),
    );
  }, [selected, searchText]);

  const columns: TableColumnsType<IFarm> = useMemo(
    () => [
      {
        title: 'ลำดับ',
        width: 60,
        align: 'center',
        render: (_, __, i) => <b>{i + 1}</b>,
      },
      {
        title: 'รหัสฟาร์ม',
        dataIndex: 'FARM_ID',
        width: 130,
        sorter: (a, b) => (a.FARM_ID || '').localeCompare(b.FARM_ID || ''),
        filters: Array.from(
          new Set(selected.map(i => i.FARM_ID).filter(Boolean)),
        )
          .sort()
          .map(v => ({ text: v as string, value: v as string })),
        onFilter: (value, record) => record.FARM_ID === value,
        filterSearch: true,
      },
      {
        title: 'รหัสเกษตรกร',
        dataIndex: 'FARM_USER1',
        width: 130,
        sorter: (a, b) =>
          (a.FARM_USER1 || '').localeCompare(b.FARM_USER1 || ''),
        filters: Array.from(
          new Set(selected.map(i => i.FARM_USER1).filter(Boolean)),
        )
          .sort()
          .map(v => ({ text: v as string, value: v as string })),
        onFilter: (value, record) => record.FARM_USER1 === value,
        filterSearch: true,
      },
      {
        title: 'ชื่อ-นามสกุล',
        dataIndex: 'FRM_NAME',
        width: 180,
        sorter: (a, b) => (a.FRM_NAME || '').localeCompare(b.FRM_NAME || ''),
        filters: Array.from(
          new Set(selected.map(i => i.FRM_NAME).filter(Boolean)),
        )
          .sort()
          .map(v => ({ text: v as string, value: v as string })),
        onFilter: (value, record) => record.FRM_NAME === value,
        filterSearch: true,
      },
      {
        title: 'ชื่อสวน',
        dataIndex: 'FARM_NAME',
        width: 180,
        sorter: (a, b) => (a.FARM_NAME || '').localeCompare(b.FARM_NAME || ''),
        filters: Array.from(
          new Set(selected.map(i => i.FARM_NAME).filter(Boolean)),
        )
          .sort()
          .map(v => ({ text: v as string, value: v as string })),
        onFilter: (value, record) => record.FARM_NAME === value,
        filterSearch: true,
      },
      {
        title: 'ตำบลที่ตั้งสวน',
        dataIndex: 'FARM_DISTRICT',
        width: 140,
        sorter: (a, b) =>
          (a.FARM_DISTRICT || '').localeCompare(b.FARM_DISTRICT || ''),
        filters: Array.from(
          new Set(selected.map(i => i.FARM_DISTRICT).filter(Boolean)),
        )
          .sort()
          .map(v => ({ text: v as string, value: v as string })),
        onFilter: (value, record) => record.FARM_DISTRICT === value,
        filterSearch: true,
      },
      {
        title: 'อำเภอที่ตั้งสวน',
        dataIndex: 'FARM_AMPHUR',
        width: 140,
        sorter: (a, b) =>
          (a.FARM_AMPHUR || '').localeCompare(b.FARM_AMPHUR || ''),
        filters: Array.from(
          new Set(selected.map(i => i.FARM_AMPHUR).filter(Boolean)),
        )
          .sort()
          .map(v => ({ text: v as string, value: v as string })),
        onFilter: (value, record) => record.FARM_AMPHUR === value,
        filterSearch: true,
      },
      {
        title: 'จังหวัดที่ตั้งสวน',
        dataIndex: 'FARM_PROVINCE',
        width: 140,
        sorter: (a, b) =>
          (a.FARM_PROVINCE || '').localeCompare(b.FARM_PROVINCE || ''),
        filters: Array.from(
          new Set(selected.map(i => i.FARM_PROVINCE).filter(Boolean)),
        )
          .sort()
          .map(v => ({ text: v as string, value: v as string })),
        onFilter: (value, record) => record.FARM_PROVINCE === value,
        filterSearch: true,
      },
      { title: 'ละติจูด', dataIndex: 'FARM_LAT', width: 110 },
      { title: 'ลองติจูด', dataIndex: 'FARM_LNG', width: 110 },
    ],
    [selected],
  );

  useEffect(() => {
    const listMap = {
      create: data?.insert,
      update: data?.update,
      match: data?.match,
    };
    const currentList = listMap[tab] || [];
    setSelected(currentList.map(c => ({ ...c, _key: uniqueId('farm-') })));
    setSelectedRowKeys([]);
    setSelectedRows([]);
  }, [tab, data]);

  const isPending = actionFarmMutation.isPending || actionDeleteFarm.isPending;

  return (
    <div style={{ padding: '20px' }}>
      <Flex justify="space-between" align="center" className="mb-3">
        <Radio.Group
          value={tab}
          onChange={e => setTab(e.target.value)}
          buttonStyle="solid"
        >
          <Radio.Button value="create">เพิ่ม</Radio.Button>
          <Radio.Button value="update">แก้ไข/ลบ</Radio.Button>
        </Radio.Group>
        <Flex gap={12}>
          <Input.Search
            placeholder="ค้นหา (ฟาร์ม/เกษตรกร/ชื่อ/ชื่อสวน)"
            allowClear
            onSearch={setSearchText}
            onChange={e => setSearchText(e.target.value)}
            style={{ width: 350 }}
          />
          {tab === 'update' && (
            <button
              type="button"
              className="btn btn-outline-danger"
              disabled={!selectedRowKeys.length || isPending}
              onClick={() => actionDeleteFarm.mutate(selectedRows)}
            >
              ลบข้อมูล Farm
            </button>
          )}
          <button
            type="button"
            className="btn btn-success"
            disabled={!selectedRowKeys.length || isPending}
            onClick={() => actionFarmMutation.mutate(selectedRows)}
          >
            {isPending
              ? 'กำลังดำเนินการ...'
              : tab === 'create'
                ? 'ส่งข้อมูลไป OTOD >'
                : 'แก้ไขข้อมูลที่ OTOD >'}
          </button>
        </Flex>
      </Flex>
      <Table<IFarm>
        rowSelection={{
          selectedRowKeys,
          onChange: (keys, rows) => {
            setSelectedRowKeys(keys);
            setSelectedRows(rows);
          },
        }}
        columns={columns}
        dataSource={filteredData}
        loading={isFetching}
        size="small"
        bordered
        rowKey="_key"
        pagination={false}
        scroll={{ x: 1400 }}
        sticky={{ offsetHeader: 0 }}
      />
    </div>
  );
};

export default FarmPage;
